import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';

import { Button, Divider, GrowlNotification, Gutter } from '@gaiads/telia-react-component-library';

import { assetReportClient } from '../../../../api-client/asset-admin-client';
import { productServiceReportsClient } from '../../../../api-client/product-service-client';

import { Loader } from '../../Loader';
import PageTemplate, { PageTemplateTranslations } from '../PageTemplate/PageTemplate';
import { isReportAccessForbidden } from '../utils';
import ReportWithDatePicker from './components/ReportWithDatePicker';
import { orderAdminReportClient } from '../../../../api-client/order-admin-client';

export default function ReportsPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const history = useHistory();

  const { t } = useTranslation('backOffice');
  const pageTemplateTranslations: PageTemplateTranslations = {
    title: t('reportsPage.title'),
  };

  const { accounts } = useMsal();

  useEffect(() => {
    if (isReportAccessForbidden(accounts)) {
      history.replace('/');
    }
  }, [accounts, history]);

  const downloadFile = (fileName: string, data: string) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
  };

  const handleReportDownload = async (getData: () => Promise<{ data: string }>, fileName: string) => {
    try {
      setLoading(true);
      const { data } = await getData();
      downloadFile(fileName, data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTemplate
      translations={pageTemplateTranslations}
      pageContent={
        <>
          <Divider />
          <Gutter size="layout-5" />

          <Button
            variant="primary"
            onClick={() => handleReportDownload(() => assetReportClient.reportAssets(), `cdaas-asset-report_${new Date().toISOString()}.csv`)}
            disabled={loading}
          >
            {t('reportsPage.assetReportButton')}
          </Button>

          <Gutter size="layout-5" />
          <Divider />
          <Gutter size="layout-5" />

          <Button
            variant="primary"
            onClick={() => handleReportDownload(() => assetReportClient.reportUsers(), `cdaas-user-report_${new Date().toISOString()}.csv`)}
            disabled={loading}
          >
            {t('reportsPage.userReportButton')}
          </Button>

          <Gutter size="layout-5" />
          <Divider />
          <Gutter size="layout-5" />

          <Button
            variant="primary"
            onClick={() =>
              handleReportDownload(() => assetReportClient.reportOrganizations(), `cdaas-organization-report_${new Date().toISOString()}.csv`)
            }
            disabled={loading}
          >
            {t('reportsPage.organizationReportButton')}
          </Button>

          <Gutter size="layout-5" />
          <Divider />
          <Gutter size="layout-5" />

          <Button
            variant="primary"
            onClick={() =>
              handleReportDownload(
                () => productServiceReportsClient.getSalesChannelBundles(),
                `cdaas-sales-channel-bundles-report_${new Date().toISOString()}.csv`,
              )
            }
            disabled={loading}
          >
            {t('reportsPage.salesChannelBundlesReportButton')}
          </Button>

          <Gutter size="layout-5" />
          <Divider />
          <Gutter size="layout-5" />

          <Button
            variant="primary"
            onClick={() =>
              handleReportDownload(
                () => productServiceReportsClient.getProductVariants(),
                `cdaas-product-variants-report_${new Date().toISOString()}.csv`,
              )
            }
            disabled={loading}
          >
            {t('reportsPage.productVariantsReportButton')}
          </Button>

          <Gutter size="layout-5" />
          <Divider />
          <Gutter size="layout-6" />

          <ReportWithDatePicker
            buttonText={t('reportsPage.eolReportButton')}
            dateRangeLabel={t('reportsPage.eolReportDateRangeLabel')}
            handleButtonClick={(date: [Date, Date]) =>
              handleReportDownload(
                () => assetReportClient.reportEndOfLease(date[0].toISOString(), date[1].toISOString()),
                `cdaas-eol-report_from_${date[0].toISOString()}_to_${date[1].toISOString()}_created-at_${new Date().toISOString()}.csv`,
              )
            }
            loading={loading}
          />

          <Gutter size="layout-5" />
          <Divider />
          <Gutter size="layout-6" />

          <ReportWithDatePicker
            buttonText={t('reportsPage.orderHistoryReportButton')}
            dateRangeLabel={t('reportsPage.orderHistoryReportDateRangeLabel')}
            handleButtonClick={(date: [Date, Date]) =>
              handleReportDownload(
                () => orderAdminReportClient.getOrdersReport(date[0].toISOString(), date[1].toISOString()),
                `cdaas-order-history-report_from_${date[0].toISOString()}_to_${date[1].toISOString()}_created-at_${new Date().toISOString()}.csv`,
              )
            }
            loading={loading}
          />

          <Gutter size="layout-5" />
          <Divider />
          <Gutter size="layout-5" />

          <Button
            variant="primary"
            onClick={() =>
              handleReportDownload(
                () => assetReportClient.reportFinanceSyncRequests(),
                `cdaas-telia-finance-sync-report_${new Date().toISOString()}.csv`,
              )
            }
            disabled={loading}
          >
            {t('reportsPage.financeSyncReportButton')}
          </Button>

          {loading && <Loader />}
          {error && <GrowlNotification notification={'Error while generating report'} variant={'error'} closeOnClick={() => setError(false)} />}
        </>
      }
    />
  );
}
